import React, { ChangeEvent, useMemo, useCallback, memo } from "react";
import { TColumn } from "../../lib/utils/getColumnsFromDataDictonary";
import { OmniDropDownInput } from "../../omni/dropdown";

// Define Option type for clarity
type Option = { id: string; value: string; disabled?: boolean };

interface ISelectProps {
    isLoading: boolean;
    placeHolder?: string;
    onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    columnValue: number;
    columns: TColumn[];
    style?: object;
    isMetricSelection: boolean;
    includedMetrics?: string[];
    className?: string;
}

const SelectWithCommonFieldsComponent: React.FC<ISelectProps> = ({
    isLoading = false,
    placeHolder = 'Select a value',
    onChange,
    style = {},
    columnValue,
    columns,
    isMetricSelection = false,
    includedMetrics = [],
    className = 'text-capitalize w-100 dropdown-lg'
}) => {   
    // Memoize column filtering to prevent recalculation on every render
    const { commonColumns, otherColumns } = useMemo(() => {
        const hasIncludedMetrics = includedMetrics.length > 0;
        const common: TColumn[] = [];
        const other: TColumn[] = [];

        for (let i = 0; i < columns.length; i++) {
            const col = columns[i];
            const { IsMetric, IsCommon, Name } = col;
            
            // Check if column should be included
            const shouldInclude = IsMetric === isMetricSelection || 
                (hasIncludedMetrics && includedMetrics.includes(Name.toLowerCase()));
            
            if (shouldInclude) {
                if (IsCommon) {
                    common.push(col);
                } else {
                    other.push(col);
                }
            }
        }

        return { commonColumns: common, otherColumns: other };
    }, [columns, isMetricSelection, includedMetrics]);

    const totalColumnCount = useMemo(() => 
        commonColumns.length + otherColumns.length,
        [commonColumns.length, otherColumns.length]
    );

    // Memoize options array construction
    const options: Option[] = useMemo(() => {
        const opts: Option[] = [
            {
                id: "-1",
                value: isLoading ? 'Loading...' : placeHolder,
                disabled: true,
            }
        ];

        // Add common columns
        for (let i = 0; i < commonColumns.length; i++) {
            const { DisplayName, TableName } = commonColumns[i];
            opts.push({
                id: i.toString(),
                value: `${DisplayName}${TableName == null ? '' : ' (' + TableName + ')'}`,
            });
        }

        // Add separator if there are other columns
        if (otherColumns.length > 0) {
            opts.push({
                id: "separator",
                value: "-----------------",
                disabled: true,
            });
        }

        // Add other columns
        for (let i = 0; i < otherColumns.length; i++) {
            const { DisplayName, TableName } = otherColumns[i];
            opts.push({
                id: (i + commonColumns.length).toString(),
                value: `${DisplayName} (${TableName})`,
            });
        }

        return opts;
    }, [commonColumns, otherColumns, isLoading, placeHolder]);

    // Memoize selectable options (without disabled/separator)
    const selectableOptions = useMemo(() => 
        options.filter((opt) => !opt.disabled && opt.id !== "separator"),
        [options]
    );

    // Memoize selected value
    const value: Option[] = useMemo(() => {
        const columnValueStr = columnValue.toString();
        const selectedOption = selectableOptions.find((opt) => opt.id === columnValueStr);
        return selectedOption ? [selectedOption] : [];
    }, [selectableOptions, columnValue]);

    // Memoize label tooltip
    const labelTooltip = useMemo(() => 
        value.length > 0 ? value[0].value : '',
        [value]
    );

    // Optimize onChange handler
    const handleValueChange = useCallback((e: CustomEvent) => {
        const selectedId = e.detail?.id ?? "-1";
        onChange({
            target: { value: selectedId },
        } as unknown as ChangeEvent<HTMLSelectElement>);
    }, [onChange]);

    // Memoize disabled state
    const isDisabled = useMemo(() => 
        isLoading || totalColumnCount === 0,
        [isLoading, totalColumnCount]
    );

    // Memoize placeholder
    const placeholderText = useMemo(() => 
        isLoading ? 'Loading...' : placeHolder,
        [isLoading, placeHolder]
    );

    return (
        <OmniDropDownInput
            className={className}
            value={value}
            options={options}
            onValueChange={handleValueChange}
            placeholder={placeholderText}
            disabled={isDisabled}
            hidefooter
            labelTooltip={labelTooltip}
            tooltipOnOverflow
            preventLastRemoval
            searchindropdown
        />
    );
};

export const SelectWithCommonFields = memo(SelectWithCommonFieldsComponent);